import { v4 as uuidv4 } from 'uuid';
import { trackPromise } from 'react-promise-tracker';
import { logger } from '../helpers/logger';
import { toQueryString } from '../helpers/queryStrings';
import { safeParseJson } from '../helpers/stringHelpers';
import { checkForNetworkError } from '../helpers/errorHelpers';
import { PortalEvents } from '../enums';
import eventEmitter from '../events/EventEmitter';
import ServerError from './ServerError';
import UnauthorizedError from './UnauthorizedError';
import NetworkError from './NetworkError';
import { ValidationError } from './ValidationError';

const runningRequests = [];

// to allow for debugging
if (process.env.NODE_ENV !== 'production') {
    window.runningRequests = runningRequests;
}
export class ApiService {
    constructor(history) {
        this.history = history;
        this.events = eventEmitter;
    }

    static requestOptions = {
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
    };

    /**
     * Guards for direct blob transfers. Both bound *inactivity*, never total duration:
     * download time scales with document size and bandwidth, so a wall-clock deadline
     * would fail large documents on slow connections rather than genuine hangs.
     */
    static blobTransferTimeouts = {
        headersMs: 15000, // no response headers at all -- size-independent, safe to bound
        stallMs: 15000, // headers arrived, then zero bytes for this long
    };

    setHost(host) {
        this.host = host;
    }

    setTokenResolver(tokenFunc) {
        this.tokenFunc = tokenFunc;
    }

    setHistory(history) {
        this.history = history;
    }

    buildHeaders() {
        var headers = {
            'Content-Type': 'application/json',
            Pragma: 'no-cache',
        };
        const frontendVersion = process.env.REACT_APP_VERSION;
        headers['CC-Frontend-Version'] = frontendVersion ?? 'not available';

        if (this.tokenFunc) {
            const token = this.tokenFunc();
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }
        return headers;
    }

    createResponseError(message, url, response, additionalProps = {}) {
        const error = new Error(message);
        error.url = url;
        error.status = response.status;
        error.contentType = response.headers.get('content-type');

        // Add any additional properties
        Object.assign(error, additionalProps);

        return error;
    }

    handleUnauthorizedResponse(response) {
        this.events.emit(PortalEvents.Unauthorized, response);
        throw new UnauthorizedError();
    }

    handleNetworkError(error) {
        this.events.emit(PortalEvents.NetworkError, error);
    }

    checkForServerError(response, url) {
        // 401 invalid token or no token
        // 403 token has been previously invalidated (ex: logged out)
        if (response.status === 401 || response.status === 403) {
            this.handleUnauthorizedResponse(response);
        }

        if (response.status !== 200 || !response.ok) {
            const ex = new ServerError(response, url);
            if (response.status === 503 || response.status === 504) ex.isTimeoutError = true;
            throw ex;
        }
    }

    checkForAppError(result, data, onAppError) {
        if (!result) return;
        if (result.wasSuccessful === false) {
            if (onAppError && typeof onAppError === 'function') {
                onAppError(result, data);
            } else {
                if (result.validationErrors && result.validationErrors.unauthorized) {
                    this.handleUnauthorizedResponse(null);
                }

                if (result.message && result.message !== null && result.message !== undefined) {
                    throw new ValidationError(result);
                }

                if (result.validationErrors && result.validationErrors instanceof Object) {
                    Object.keys(result.validationErrors).forEach((key) => {
                        let validationMsg = result.validationErrors[key];
                        logger.warn(`Server Validation Error [${key}]: ${validationMsg}`);
                    });
                }
            }
        }
    }

    async handleJsonResponse(response, url) {
        // Check if response has content before trying to parse JSON
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');

        // If no content or not JSON, throw specific error for missing JSON
        if (contentLength === '0' || !contentType || !contentType?.includes('application/json')) {
            throw this.createResponseError(
                'Successful fetch with missing JSON response - expected JSON content but received empty or non-JSON response',
                url,
                response,
                { contentLength },
            );
        }

        // Try to parse JSON with error handling
        try {
            const responseText = await response.text();
            if (!responseText?.trim()) {
                throw this.createResponseError(
                    'Successful fetch with empty JSON response - expected JSON content but received empty response body',
                    url,
                    response,
                );
            }
            return JSON.parse(responseText);
        } catch (jsonError) {
            // Handle JSON parsing errors
            if (
                jsonError instanceof Error &&
                jsonError?.message?.includes('Successful fetch with')
            ) {
                // Re-throw our custom response errors
                throw jsonError;
            }

            throw this.createResponseError(
                'Successful fetch with malformed JSON response - received invalid JSON that could not be parsed',
                url,
                response,
                { cause: jsonError },
            );
        }
    }

    getFileNameFromDispositionHeader(header) {
        let contentDispostion = header.split(';');
        const fileNameToken = `filename*=UTF-8''`;
        let fileName = 'downloaded.pdf';
        for (let thisValue of contentDispostion) {
            if (thisValue.trim().indexOf(fileNameToken) === 0) {
                fileName = decodeURIComponent(thisValue.trim().replace(fileNameToken, ''));
                break;
            }
        }
        return fileName;
    }

    // bypass the usual pipeline to issue very simple request
    async issueSlimRequest(path, method, data, overrideUrl) {
        const queryString = method === 'GET' && data ? '?' + toQueryString(data) : '';
        const url = `${this.host}${path}${queryString}`;
        const body = method === 'POST' ? data : undefined;

        try {
            const response = await fetch(url, {
                ...this.constructor.requestOptions,
                method,
                headers: this.buildHeaders(),
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include',
            });
            return { response, error: null };
        } catch (error) {
            // For network errors, create a structured NetworkError for consistency
            if (checkForNetworkError(error)) {
                const networkError = new NetworkError(error, url);
                return { response: null, error: networkError };
            }

            // Add URL context to other errors for better Sentry reporting
            if (!error?.url) error.url = url;
            return { response: null, error };
        }
    }

    /**
     * Converts an aborted transfer into a reportable timeout, or passes a genuine
     * cancellation through untouched so callers can stay silent about it.
     */
    createBlobTransferTimeoutError(error, timedOutPhase, { headersMs, stallMs }, url) {
        // No phase recorded means the caller cancelled, or the fetch failed for its
        // own reasons. Either way it is not ours to relabel.
        if (!timedOutPhase) return error;

        const seconds = (milliseconds) => Math.round(milliseconds / 1000);
        const timeoutError = new Error(
            timedOutPhase === 'headers'
                ? `The document server did not respond within ${seconds(headersMs)} seconds.`
                : `The document transfer stalled for ${seconds(stallMs)} seconds.`,
        );

        // Deliberately not 'AbortError': that name is reserved for cancellation, which
        // handleApiFailure swallows. isTimeoutError routes this to a '-TO' error code so
        // FriendlyErrorDisplay renders its timeout messaging rather than a generic failure.
        timeoutError.name = 'BlobTransferTimeoutError';
        timeoutError.isTimeoutError = true;
        timeoutError.timedOutPhase = timedOutPhase;
        timeoutError.url = url;

        return timeoutError;
    }

    /**
     * Buffers the response body while guarding against a stalled socket. Total duration is
     * deliberately not capped: download time scales with document size and bandwidth, so a
     * large chart on a slow link must be allowed to finish. Only inactivity is bounded --
     * a healthy transfer keeps delivering chunks and keeps resetting the guard.
     */
    async readBlobWithStallGuard(response, contentType, armStallGuard, disarmGuard) {
        if (!response.body?.getReader) {
            // Non-streaming environments cannot detect a stall; drop the guard entirely
            // rather than letting the headers deadline expire mid-download.
            disarmGuard();
            return await response.blob();
        }

        const reader = response.body.getReader();
        const chunks = [];

        armStallGuard();
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) chunks.push(value);
            armStallGuard();
        }

        return new Blob(chunks, { type: contentType });
    }

    async loadBlobAsObjectUrl(url, abortSignal, timeoutOverrides) {
        const { headersMs, stallMs } = {
            ...this.constructor.blobTransferTimeouts,
            ...timeoutOverrides,
        };

        const controller = new AbortController();
        const forwardAbort = () => controller.abort();
        let timedOutPhase = null;
        let timer = null;

        const armGuard = (milliseconds, phase) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                timedOutPhase = phase;
                controller.abort();
            }, milliseconds);
        };
        const disarmGuard = () => clearTimeout(timer);
        const releaseGuards = () => {
            disarmGuard();
            abortSignal?.removeEventListener?.('abort', forwardAbort);
        };

        if (abortSignal?.aborted) controller.abort();
        else abortSignal?.addEventListener?.('abort', forwardAbort, { once: true });

        const timeouts = { headersMs, stallMs };
        let response;
        try {
            // Bound time-to-first-byte only. TTFB does not scale with document size, so
            // this can never fire on a slow-but-healthy transfer of a large document.
            armGuard(headersMs, 'headers');

            response = await fetch(url, {
                ...this.constructor.requestOptions,
                method: 'GET',
                credentials: 'omit',
                redirect: 'follow',
                referrerPolicy: 'no-referrer',
                signal: controller.signal,
            });
        } catch (error) {
            releaseGuards();
            throw this.createBlobTransferTimeoutError(error, timedOutPhase, timeouts, url);
        }

        const responseUrl = response.url || url;
        let safeUrl;
        try {
            const parsedUrl = new URL(responseUrl);
            safeUrl = `${parsedUrl.origin}${parsedUrl.pathname}`;
        } catch {
            safeUrl = responseUrl.split('?')[0];
        }

        if (!response.ok) {
            releaseGuards();
            throw new ServerError(
                {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    json: response.json?.bind(response),
                    url: safeUrl,
                },
                safeUrl,
            );
        }

        const contentType = response.headers.get('content-type');
        if (!contentType?.toLowerCase().includes('application/pdf')) {
            releaseGuards();
            throw this.createResponseError(
                'Successful fetch returned non-PDF content.',
                safeUrl,
                response,
            );
        }

        let blob;
        try {
            blob = await this.readBlobWithStallGuard(
                response,
                contentType,
                () => armGuard(stallMs, 'stall'),
                disarmGuard,
            );
        } catch (error) {
            throw this.createBlobTransferTimeoutError(error, timedOutPhase, timeouts, safeUrl);
        } finally {
            releaseGuards();
        }

        return {
            wasSuccessful: true,
            validationErrors: null,
            blobUrl: URL.createObjectURL(blob),
        };
    }

    async issueRequest(path, method, data, abortSignal, onAppError) {
        const queryString = method === 'GET' && data ? '?' + toQueryString(data) : '';
        const url = `${this.host}${path}${queryString}`;
        const body = method === 'POST' ? data : undefined;

        try {
            const response = await fetch(url, {
                signal: abortSignal || null,
                ...this.constructor.requestOptions,
                method,
                headers: this.buildHeaders(),
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include',
            });

            this.checkForServerError(response, url);

            const dispositionHeader = response.headers.get('content-disposition');
            if (dispositionHeader) {
                const fileName = this.getFileNameFromDispositionHeader(dispositionHeader);
                const blob = await response.blob();

                var blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = fileName;
                link.click();

                return Promise.resolve({ wasSuccessful: true, validationErrors: null });
            }

            const result = await this.handleJsonResponse(response, url);

            this.checkForAppError(result, data, onAppError);

            return result;
        } catch (error) {
            // if request was aborted, throw error, will be handled by issueCancelableRequest
            if (error.name === 'AbortError') throw error;

            logger.error(error);

            if (error.getServerStackTrace) {
                const stackTrace = await error.getServerStackTrace();
                stackTrace && logger.error('Server Error Stacktrace:', stackTrace);
            }

            if (checkForNetworkError(error)) {
                if (process.env.NODE_ENV !== 'production')
                    logger.error(
                        'Failure to communicate with API. Ensure that the asp.net core web API is running.',
                    );

                // Create a structured NetworkError for better reporting (similar to ServerError)
                const networkError = new NetworkError(error, url);
                this.handleNetworkError(networkError);
                throw networkError;
            }

            if (error instanceof ServerError) {
                // if error has JSON data, try to parse it because it may contain an error code
                if (error.json) {
                    await error.json();
                }
                throw error; // rethrow original error
            } else if (error instanceof UnauthorizedError) {
                throw error; // rethrow original error
            }

            // For other errors, add URL context and use default error message
            const enhancedError = new Error(ServerError.DefaultErrorMessage, { cause: error });
            enhancedError.url = url;
            enhancedError.originalError = error;
            throw enhancedError;
        }
    }

    async issueCancelableRequest(path, method, data, cancelationOptions, onAppError) {
        const { id, noParallelRequests, allowAbortError } = cancelationOptions;

        if (!id) {
            throw new Error('cancelationOptions.id should be set');
        }

        if (noParallelRequests === null || noParallelRequests === undefined) {
            throw new Error('cancelationOptions.noParallelRequests should be set');
        } else if (noParallelRequests === false) {
            logger.warn(
                'issueCancelableRequest has been invoked with noParallelRequests set to false, did you mean to set it to true?',
            );
            return trackPromise(this.issueRequest(path, method, data, null, onAppError), 'API');
        }

        // initialize to empty array if key doesn't already exist
        let previousRequests = runningRequests[id];
        if (!previousRequests) runningRequests[id] = previousRequests = [];

        // loop through all previous requests and cancel them if they are still running
        previousRequests.forEach((previousRequest) => {
            if (previousRequest.canceled) return;
            logger.debug(
                `New request initiated for ${id}, canceling previous request with ID: ${previousRequest.requestId}`,
            );
            previousRequest.cancel();
        });

        const abortController = new AbortController();
        const requestId = uuidv4();

        previousRequests.push({
            requestId,
            canceled: false,
            cancel() {
                this.canceled = true;
                abortController.abort();
            },
        });

        return trackPromise(
            this.issueRequest(path, method, data, abortController.signal, onAppError)
                .catch((error) => {
                    // if the request was aborted and should not throw error then return undefined
                    if (error.name === 'AbortError' && !allowAbortError) {
                        logger.debug(
                            `Request was aborted for ${id} but allowAbortError was false. Returning undefined.`,
                        );
                        return undefined;
                    }

                    throw error;
                })
                .finally(() => {
                    // delete request from array
                    const index = previousRequests.findIndex((r) => r.requestId === requestId);
                    previousRequests.splice(index, 1);
                }),
            'API',
        );
    }

    async get(path, data, cancelationOptions, onAppError) {
        return cancelationOptions
            ? this.issueCancelableRequest(path, 'GET', data, cancelationOptions, onAppError)
            : trackPromise(this.issueRequest(path, 'GET', data, null, onAppError), 'API');
    }

    async post(path, data, onAppError) {
        return trackPromise(this.issueRequest(path, 'POST', data, null, onAppError), 'API');
    }

    async postMultipartForm(path, data, files, onProgress) {
        try {
            return await this.issueXMLHttpRequest(path, data, files, onProgress);
        } catch (e) {
            throw new Error(
                'There was a problem uploading your file. It may be too large (over 100MB) or corrupt. Please check your file and try again.',
            );
        }
    }

    issueXMLHttpRequest(path, data, files, onProgress) {
        const url = `${this.host}${path}`;

        return new Promise((accept, reject) => {
            let formData = new FormData();

            // add model to formData instance
            formData.append('model', JSON.stringify(data));

            // add files to formData instance
            if (files && files.length > 0) {
                if (files.length > 1)
                    files.forEach((file, i) => formData.append('file' + ++i, file, file.name));
                else formData.append('file', files[0], files[0].name);
            }

            let xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);

            let headers = this.buildHeaders();

            Object.keys(headers)
                .filter((h) => h !== 'Content-Type')
                .forEach((header) => {
                    xhr.setRequestHeader(header, headers[header]);
                });

            if (onProgress) {
                xhr.upload.onprogress = function (e) {
                    if (e.lengthComputable) {
                        let percentComplete = Math.round((e.loaded / e.total) * 100);
                        onProgress(percentComplete);
                    }
                };
            }

            xhr.onload = function () {
                if (this.status === 200) {
                    const parsedResult = safeParseJson(this.response);
                    accept(parsedResult);
                } else {
                    reject(this.response);
                }
            };

            xhr.onerror = function (e) {
                reject(e);
            };

            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    // used for mock data
    wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}

export default ApiService;
